import { UserRoleEnum } from '@vacationtracker/shared/types/user'

export interface VeePageSuggestion {
  kbSlug: string
  titleKey: string
  askPhraseKey?: string
  roles?: UserRoleEnum[]
}

const dashboardOverview: VeePageSuggestion = {
  kbSlug: 'how-do-i-access-the-dashboard',
  titleKey: 'veeAssistant.pageQ.dashboardOverview',
}

const addLeaveForUser: VeePageSuggestion = {
  kbSlug: 'how-do-i-add-a-leave-for-a-user',
  titleKey: 'veeAssistant.pageQ.addLeaveForUser',
  askPhraseKey: 'veeAssistant.pageQ.addLeaveForUser.ask',
}

const scheduledReportsEmail: VeePageSuggestion = {
  kbSlug: 'guide-how-do-i-set-scheduled-reports',
  titleKey: 'veeAssistant.pageQ.scheduledReportsEmail',
  roles: [UserRoleEnum.Admin, UserRoleEnum.LocationAdmin],
}

const requestLeave: VeePageSuggestion = {
  kbSlug: 'how-do-i-request-a-leave',
  titleKey: 'veeAssistant.pageQ.requestLeave',
  askPhraseKey: 'veeAssistant.pageQ.requestLeave.ask',
}

const myCalendarSync: VeePageSuggestion = {
  kbSlug: 'guide-how-do-i-sync-my-calendar-with-vacation-tracker',
  titleKey: 'veeAssistant.pageQ.myCalendarSync',
}

const toil: VeePageSuggestion = {
  kbSlug: 'how-do-i-request-toil-time-off-in-lieucomp-time',
  titleKey: 'veeAssistant.pageQ.toil',
  askPhraseKey: 'veeAssistant.pageQ.toil.ask',
}

const rolloverPolicy: VeePageSuggestion = {
  kbSlug: 'how-do-i-set-a-brought-forward-rollover-policy',
  titleKey: 'veeAssistant.pageQ.rolloverPolicy',
}

const calendarView: VeePageSuggestion = {
  kbSlug: 'what-information-can-i-see-on-my-calendar-view',
  titleKey: 'veeAssistant.pageQ.calendarView',
}

const calendarIcal: VeePageSuggestion = {
  kbSlug: 'adding-a-new-calendar-using-ical-connection',
  titleKey: 'veeAssistant.pageQ.calendarIcal',
}

const leaveBalancesExport: VeePageSuggestion = {
  kbSlug: 'how-do-i-export-employee-leave-balances',
  titleKey: 'veeAssistant.pageQ.leaveBalancesExport',
}

const leavePolicies: VeePageSuggestion = {
  kbSlug: 'how-do-i-create-and-configure-leave-policies',
  titleKey: 'veeAssistant.pageQ.leavePolicies',
}

const leaveTypes: VeePageSuggestion = {
  kbSlug: 'how-can-i-create-and-configure-leave-types',
  titleKey: 'veeAssistant.pageQ.leaveTypes',
}

const homeEntries: VeePageSuggestion[] = [dashboardOverview, addLeaveForUser, scheduledReportsEmail]

const requestLeaveEntries: VeePageSuggestion[] = [
  { kbSlug: 'how-do-i-request-a-leave', titleKey: 'veeAssistant.pageQ.requestLeaveHow' },
  { kbSlug: 'how-do-i-set-a-brought-forward-rollover-policy', titleKey: 'veeAssistant.pageQ.rollover' },
  toil,
]

const importEntries: VeePageSuggestion[] = [
  {
    kbSlug: 'how-can-i-import-my-leave-data-into-vacation-tracker-leave-data-import',
    titleKey: 'veeAssistant.pageQ.leaveDataImport',
    askPhraseKey: 'veeAssistant.pageQ.leaveDataImport.ask',
  },
  { kbSlug: 'data-import-importing-leave-data-using-our-template', titleKey: 'veeAssistant.pageQ.importTemplate' },
  { kbSlug: 'how-do-import-time-off-in-lieu-balances', titleKey: 'veeAssistant.pageQ.toilImport' },
]

export const VEE_PAGE_SUGGESTIONS: Record<string, VeePageSuggestion[]> = {
  '/app/home': homeEntries,
  '/app/dashboard': homeEntries,
  '/app/my-dashboard': [requestLeave, myCalendarSync, toil],
  '/app/leaves/request-leave': requestLeaveEntries,
  '/app/request-leave': requestLeaveEntries,
  '/app/leaves/add-leave': [addLeaveForUser],
  '/app/calendar': [calendarView, { kbSlug: 'guide-how-do-i-sync-my-calendar-with-vacation-tracker', titleKey: 'veeAssistant.pageQ.calendarSyncProviders' }, calendarIcal],
  '/app/calendar/sync': [{ kbSlug: 'guide-how-do-i-sync-my-calendar-with-vacation-tracker', titleKey: 'veeAssistant.pageQ.calendarSyncHow' }, calendarIcal],
  '/app/wallchart': [
    { kbSlug: 'how-can-i-use-wallchart-to-view-upcoming-leaves', titleKey: 'veeAssistant.pageQ.wallchart' },
    calendarView,
    {
      kbSlug: 'guide-notifications-within-vacation-tracker',
      titleKey: 'veeAssistant.pageQ.teamLeaveNotifications',
      roles: [UserRoleEnum.Admin, UserRoleEnum.LocationAdmin, UserRoleEnum.Approver],
    },
  ],
  '/app/holidays': [
    { kbSlug: 'how-do-i-set-holidays', titleKey: 'veeAssistant.pageQ.holidaysSetup' },
    { kbSlug: 'what-information-can-i-see-on-my-calendar-view', titleKey: 'veeAssistant.pageQ.holidaysOnCalendar' },
  ],
  '/app/import-holidays': [
    {
      kbSlug: 'how-do-i-set-holidays',
      titleKey: 'veeAssistant.pageQ.holidaysImport',
      roles: [UserRoleEnum.Admin, UserRoleEnum.LocationAdmin],
    },
  ],
  '/app/notifications': [
    { kbSlug: 'guide-notifications-within-vacation-tracker', titleKey: 'veeAssistant.pageQ.notifications' },
    {
      kbSlug: 'guide-how-do-i-set-scheduled-reports',
      titleKey: 'veeAssistant.pageQ.notificationsScheduledReports',
      roles: [UserRoleEnum.Admin, UserRoleEnum.LocationAdmin],
    },
  ],
  '/app/add-ons': [{ kbSlug: 'how-do-i-set-a-probation-period-add-on', titleKey: 'veeAssistant.pageQ.probationAddon' }],
  '/app/reports': [
    { kbSlug: 'what-types-of-reports-can-i-export', titleKey: 'veeAssistant.pageQ.reportTypes' },
    leaveBalancesExport,
    { kbSlug: 'guide-how-do-i-set-scheduled-reports', titleKey: 'veeAssistant.pageQ.scheduledReportsRecurring' },
  ],
  '/app/reports/leave-history-report': [
    { kbSlug: 'how-do-i-export-a-leave-request-report-leave-history', titleKey: 'veeAssistant.pageQ.leaveHistoryExport' },
    { kbSlug: 'guide-how-do-i-set-scheduled-reports', titleKey: 'veeAssistant.pageQ.scheduledReportsThis' },
  ],
  '/app/reports/leave-balance-report': [leaveBalancesExport],
  '/app/reports/leave-balance-monthly-report': [leaveBalancesExport],
  '/app/reports/company-logs': [{ kbSlug: 'how-can-i-view-company-logs', titleKey: 'veeAssistant.pageQ.companyLogs' }],
  '/app/reports/scheduled-reports': [{ kbSlug: 'guide-how-do-i-set-scheduled-reports', titleKey: 'veeAssistant.pageQ.scheduledReportsSetup' }],
  '/app/users': [
    {
      kbSlug: 'how-do-i-add-a-leave-for-a-user',
      titleKey: 'veeAssistant.pageQ.addLeaveForUserAlt',
      askPhraseKey: 'veeAssistant.pageQ.addLeaveForUser.ask',
    },
    { kbSlug: 'how-do-i-add-import-users', titleKey: 'veeAssistant.pageQ.addImportUsers', roles: [UserRoleEnum.Admin] },
    { kbSlug: 'how-do-i-import-users-profile-data', titleKey: 'veeAssistant.pageQ.importProfileData', roles: [UserRoleEnum.Admin] },
  ],
  '/app/users/import': [
    { kbSlug: 'how-do-i-add-import-users', titleKey: 'veeAssistant.pageQ.userImportHow', roles: [UserRoleEnum.Admin] },
    { kbSlug: 'how-do-i-import-users-profile-data', titleKey: 'veeAssistant.pageQ.importProfileData', roles: [UserRoleEnum.Admin] },
  ],
  '/app/settings/general': [{ kbSlug: 'how-do-i-change-the-company-name-on-the-online-dashboard', titleKey: 'veeAssistant.pageQ.companyName' }],
  '/app/settings/departments': [
    { kbSlug: 'how-do-i-create-a-department-within-vacation-tracker', titleKey: 'veeAssistant.pageQ.createDepartment' },
    { kbSlug: 'how-do-i-edit-department-information', titleKey: 'veeAssistant.pageQ.editDepartment' },
  ],
  '/app/settings/locations': [
    { kbSlug: 'how-do-i-create-a-location', titleKey: 'veeAssistant.pageQ.createLocation', roles: [UserRoleEnum.Admin] },
    { kbSlug: 'how-do-i-set-holidays', titleKey: 'veeAssistant.pageQ.holidaysForLocation' },
    rolloverPolicy,
  ],
  '/app/settings/location': [rolloverPolicy, { kbSlug: 'how-do-i-create-and-configure-leave-policies', titleKey: 'veeAssistant.pageQ.configureLeavePolicy' }],
  '/app/settings/leave-types': [
    leaveTypes,
    {
      kbSlug: 'how-do-i-create-and-configure-leave-policies',
      titleKey: 'veeAssistant.pageQ.typesVsPolicies',
      askPhraseKey: 'veeAssistant.pageQ.typesVsPolicies.ask',
    },
  ],
  '/app/settings/leave-policies': [leavePolicies, rolloverPolicy, leaveTypes],
  '/app/settings/billing': [{ kbSlug: 'how-do-i-access-my-customer-portal-billing-information', titleKey: 'veeAssistant.pageQ.billing' }],
  '/app/settings/personal': [
    { kbSlug: 'how-do-i-choose-my-view-preferences', titleKey: 'veeAssistant.pageQ.viewPreferences' },
    { kbSlug: 'guide-how-do-i-sync-my-calendar-with-vacation-tracker', titleKey: 'veeAssistant.pageQ.calendarSyncPersonal' },
  ],
  '/app/imports': importEntries,
  '/app/import': importEntries,
  '/app/my-profile': [requestLeave, { kbSlug: 'how-do-i-choose-my-view-preferences', titleKey: 'veeAssistant.pageQ.viewPreferences' }, myCalendarSync],
}

const MAX_PAGE_SUGGESTIONS = 3

export function getPageSuggestions(pathname: string, role?: string): VeePageSuggestion[] {
  const matchedRoute = Object.keys(VEE_PAGE_SUGGESTIONS)
    .filter((route) => pathname === route || pathname.startsWith(`${route}/`))
    .sort((a, b) => b.length - a.length)[0]

  if (!matchedRoute) {
    return []
  }

  return VEE_PAGE_SUGGESTIONS[matchedRoute].filter((entry) => !entry.roles || entry.roles.includes(role as UserRoleEnum)).slice(0, MAX_PAGE_SUGGESTIONS)
}
